import { Component, Input, Output, EventEmitter, forwardRef, OnChanges, SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Subject } from 'rxjs/internal/Subject';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'si-combo-box',
  standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, MatIconModule, MatButtonModule, TranslatePipe],
  templateUrl: './si-combo-box.component.html',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SiComboBoxComponent), multi: true }]
})
export class SiComboBoxComponent implements ControlValueAccessor, OnChanges, OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();
    @Input() inputValue: string | undefined | number = '';
    @Input() isReadMode = true;
    private inputDisabled = false;
    private cvaDisabled = false;
    private showAllOptions = false;
    isFocused = false;
    @Input() dropDownList: Array<{ label: string; value: string }> = [];
    @Input() placeholder?: string;
    @Input() 
    set isDisabled(v: boolean) { 
        this.inputDisabled = !!v; 
        this.syncDisabled(); 
    }

    get isDisabled(): boolean { 
        return this.inputDisabled; 
    }
    @Output() inputValueChange = new EventEmitter<string>();

    readonly control = new FormControl<string>('');
    private onChange: (v: string) => void = () => {};
    private onTouched: () => void = () => {};

    ngOnInit(): void {
    this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(v => {
        this.showAllOptions = false;
        const value = v ?? '';
        this.onChange(value);
        this.inputValueChange.emit(value);
    });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['inputValue']) {
            this.control.setValue((this.inputValue ?? '').toString(), { emitEvent: false });
        }
    }

    writeValue(value: string | number | null): void {
        this.control.setValue(value == null 
            ? '' 
            : value.toString(), { emitEvent: false });
    }
    registerOnChange(fn: (v: string) => void): void { 
        this.onChange = fn; 
    }

    registerOnTouched(fn: () => void): void { 
        this.onTouched = fn; 
    }

    setDisabledState(isDisabled: boolean): void { 
        this.cvaDisabled = isDisabled; 
        this.syncDisabled(); 
    }

    onBlur(): void { 
        this.isFocused = false;
        this.onTouched(); 
    }

    onFocus(): void {
        this.isFocused = true;
    }

    private syncDisabled(): void {
        const disabled = this.inputDisabled || this.cvaDisabled;
        disabled 
        ? this.control.disable({ emitEvent: false }) 
        : this.control.enable({ emitEvent: false });
    }
    private filter(value: string): Array<{ label: string; value: string }> {
    if (!value || this.dropDownList.some(o => o.label === value)) return this.dropDownList;
      const lower = value.toLowerCase();
      return this.dropDownList.filter(o => o.label.toLowerCase().includes(lower));
    }
    onDropDownIconClick(trigger : MatAutocompleteTrigger): void {
        this.showAllOptions = true;
        trigger.openPanel();
    }

    get filteredOptions(): Array<{ label: string; value: string }> {
        return this.showAllOptions
        ? this.dropDownList
        : this.filter(this.control.value ?? '');
    }

    onOptionSelection(): void {
        this.onTouched();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}